﻿/**
 * Encodes a display value (a photo title or a collection name) into the URL slug used by the public site.
 *
 * The rules mirror the server-side `UrlSlug` helper: whitespace becomes a dash, characters that survive
 * `encodeURIComponent` unchanged (unreserved and sub-delimiter characters) are kept, and every other
 * character — including '#' — is dropped. Casing is preserved.
 *
 * @param value - The display value to encode.
 * @returns The encoded slug, which is empty when the value contains no URL-friendly character.
 */
export function createUrlSlug(value: string): string {
    return Array.from(value, character => {
        if (/\s/u.test(character)) return '-';
        return encodeURIComponent(character) === character ? character : '';
    }).join('');
}

/**
 * Determines whether a path segment is already a valid slug for itself, so re-encoding cannot change it.
 *
 * @param slug - The candidate path segment.
 * @returns True when the segment is non-empty and encoding it reproduces the same value.
 */
export function isValidUrlSlug(slug: string): boolean {
    return slug.length > 0 && createUrlSlug(slug) === slug;
}

/**
 * Compares a slug taken from a request path with the slug produced by a display value, ignoring case.
 * This is the shared decode step: slugs are never reversed back into text, they are re-encoded from the
 * stored value and compared.
 *
 * @param slug - The slug received from the request path.
 * @param value - The display value to compare against.
 * @returns True when the display value encodes to the supplied slug, ignoring case.
 */
export function urlSlugMatchesValue(slug: string, value: string): boolean {
    return createUrlSlug(value).toLowerCase() === slug.toLowerCase();
}
